import { orthoMetersPerPixelAtom } from '@/atoms/viewer.atom';
import { userViewerSettingsAtom } from '@/atoms/user.atom';
import { niceRound, TARGET_PIXELS } from '@/lib/orthoScale';
import { useAtomValue } from 'jotai';
import { FunctionComponent, useEffect, useRef, useState } from 'react';

interface OrthoGridProps {
  projectId: string;
}

// A single translucent grey (as the backend render uses) vanishes against
// dark point clouds, bright imagery, or mid-grey terrain — there's no one
// colour that contrasts with an arbitrary background. Instead each grid line
// is a "casing": a dark stripe paired with a light stripe (1px each), so one
// edge always stands out regardless of what's behind it.
const DARK = 'rgba(0, 0, 0, 0.30)';
const LIGHT = 'rgba(255, 255, 255, 0.45)';

// One repeating axis: a 2px dark+light line, then transparent until the next cell.
const axis = (dir: 'to right' | 'to bottom', cellPx: number) =>
  `repeating-linear-gradient(${dir}, ${DARK} 0, ${DARK} 1px, ${LIGHT} 1px, ${LIGHT} 2px, transparent 2px, transparent ${cellPx}px)`;

/**
 * Optional screen-locked reference grid for the orthographic (top-down)
 * camera. Mirrors the backend orthophoto render, which overlays a grey
 * "graph paper" grid whose cell size equals the scale bar's length.
 *
 * Cell size is computed from the same `niceRound`/`TARGET_PIXELS` the
 * `OrthoScaleBar` uses, so a grid cell is always exactly one scale-bar
 * length. The grid only renders in orthographic mode — `orthoMetersPerPixel`
 * is null for the perspective camera — and only when the user enabled the
 * `showOrthoGrid` setting.
 */
const OrthoGrid: FunctionComponent<OrthoGridProps> = ({ projectId }) => {
  const metersPerPixel = useAtomValue(orthoMetersPerPixelAtom(projectId));
  const showOrthoGrid = useAtomValue(userViewerSettingsAtom).showOrthoGrid;

  // Throttle so the grid doesn't churn every frame while zooming (mirrors
  // OrthoScaleBar).
  const [throttled, setThrottled] = useState<number | null>(metersPerPixel);
  const lastRef = useRef(0);
  useEffect(() => {
    const now = Date.now();
    if (now - lastRef.current < 100) {
      const t = setTimeout(() => {
        lastRef.current = Date.now();
        setThrottled(metersPerPixel);
      }, 100);
      return () => clearTimeout(t);
    }
    lastRef.current = now;
    setThrottled(metersPerPixel);
  }, [metersPerPixel]);

  if (!showOrthoGrid) return null;
  if (throttled == null || !isFinite(throttled) || throttled <= 0) return null;

  const { value: niceMeters } = niceRound(throttled * TARGET_PIXELS);
  const cellPx = niceMeters / throttled;
  if (!isFinite(cellPx) || cellPx < 2) return null;

  return (
    <div
      aria-hidden
      className='pointer-events-none absolute inset-0'
      style={{
        backgroundImage: `${axis('to right', cellPx)}, ${axis('to bottom', cellPx)}`,
      }}
    />
  );
};

export default OrthoGrid;
